import React from "react";
import { CampaignType, EarningRuleRewardType, EarningRuleType } from "config/CustomEnums";

import EarningRuleBirthday from 'assets/images/earning/birthday.png';
import EarningRuleGerneralPurchase from 'assets/images/earning/general_purchase.png';
import EarningRuleGPS from 'assets/images/earning/gps_check_in.png';
import EarningRuleLevelUpgrade from 'assets/images/earning/levelUpgrade.png';
import EarningRuleNewMember from 'assets/images/earning/new_member.png';
import EarningRuleQRCode from 'assets/images/earning/qrcode_scanning.png';
import EarningRuleReferral from 'assets/images/earning/referral.png';
import EarningRuleWebhook from 'assets/images/earning/webhook.png';

import TypeSelectArea from "components/base/TypeSelectArea";
import i18n from '../../../I18n.js';
import { createAction } from "utils";
import { errorMessage, hasError } from "components/base/ErrorFieldMessage";

import OverviewSection from "components/earning/OverviewSection.js";
import { isEarningRuleRequireToLinkCampaign } from "models/EarningRulesModel.js";
import LimitSection, { LevelUpgradePerHeadPeriodicLimitSection } from "components/earning/LimitSection.js";
import QRCodeScanningSection from "components/earning/QRCodeScanningSection.js";
import LevelUpgradeSection from "components/earning/LevelUpgradeSection.js";
import { CustomTitleLabel } from "components/earning/CustomBaseComponments.js";
import CouponDropdownWithButtonAndPrompt from "components/earning/CouponDropdownWithButtonAndPrompt.js";
import { getCampaignTypeSelectPromptConfig } from "components/campaign/campaignCreation/CreateCampaignStepOneConfigs.js";
import InputFieldControl from "components/base/InputFieldControl.js";
import TargetTypeCustomerSelector, { StoreMultipleSelector } from "components/earning/TargetTypeCustomerSelector.js";
import PeriodSection from "components/base/period/PeriodSection.js";
import { earningRuleAvailableClue } from "components/campaign/campaignCreation/CreateCampaignUtil.js";
import PeriodAndWeekdaySection from "components/base/period/PeriodAndWeekdaySection.js";
import ReactHookFormSwitch from "components/reactHookFormComponents/ReactHookFormSwitch.js";
import ReactHookFormMarkDownArea from "components/reactHookFormComponents/ReactHookFormMarkDownArea.js";
import ReactHookFormTitleWithRadio from "components/reactHookFormComponents/ReactHookFormTitleWithRadio.js";
import RewardLogicSelector from "components/earning/RewardLogicSelector.js";
import RewardAmountInputArea from "components/earning/RewardAmountInputArea.js";
import PurchaseEarningRuleRewardLimitInputs from "components/earning/PurchaseEarningRuleRewardLimitInputs.js";
import ReactHookFormTextInput from "components/reactHookFormComponents/ReactHookFormTextInput.js";
import RecurringCampaignPeriodSelector from 'components/campaign/campaignCreation/RecurringCampaignPeriodSelector'
import SkuTypeSelector from "components/earning/SkuTypeSelector.js";
import GeneralPurchasePerMin from "components/earning/GeneralPurchasePerMin.js";
import ReactHookFormMultipleSelector from "components/reactHookFormComponents/ReactHookFormMultipleSelector.js";

const EarningRuleTypesConfig = [
  {
    id: EarningRuleType.qrCodeScanning,
    title: 'QR Code Scanning',
    image: EarningRuleQRCode,
    // imageWidth: '101.61px',
    description:
      'Encourage customers to explore a place and scan its QR code(s)',
    customLabel: "Required to link to a campaign",
  },
  {
    id: EarningRuleType.gpsCheckIn,
    title: 'GPS Check-in',
    image: EarningRuleGPS,
    // imageWidth: '120px',
    description: 'Encourage customers to visit a place and do GPS check-in',
    customLabel: "Required to link to a campaign",
  },
  {
    id: EarningRuleType.memberReferral,
    title: 'Referral',
    image: EarningRuleReferral,
    // imageWidth: '120px',
    description: 'Encourage customers to refer new members',
    customLabel: "Required to link to a campaign",
  },

  {
    id: EarningRuleType.generalPurchase,
    title: 'General Purchase',
    image: EarningRuleGerneralPurchase,
    // imageWidth: '120px',
    description:
      'Encourage customers to purchase and claim for points or coupons',
  },
  {
    id: EarningRuleType.newMember,
    title: 'New Member',
    image: EarningRuleNewMember,
    // imageWidth: '93.54px',
    description: 'Encourage customers to sign up as new members',
  },
  {
    id: EarningRuleType.birthday,
    title: 'Birthday',
    image: EarningRuleBirthday,
    // imageWidth: '120px',
    description: 'Customers can enjoy exclusive offers on their birthday date',
  },
  {
    id: EarningRuleType.fillingForm,
    title: 'Webhook',
    image: EarningRuleWebhook,
    // imageWidth: '120px',
    description: 'Enable earning activities validation from third party',
  },
  {
    id: EarningRuleType.levelUpgrade,
    title: 'Level Upgrade',
    image: EarningRuleLevelUpgrade,
    // imageWidth: '120px',
    description: 'Enable customer to earn reward upon level upgrade',
  },
];

export const CREATION_STEP_ONE_CONFIG = {
  // extraValidate: ({ getValues, setError, clearErrors }) => true,
  getDisplayComponentKeys: ({ formContext }) => {
    return {
      displayNormalSectionComponents: ["type_selector_key", "link_stamp_campaign_switch_key"],
      displayLanguageSectionComponents: [],
    }
  },
  normalSections: [
    {
      sectionContent: [
        {
          key: "type_selector_key",
          component: TypeSelectArea,
          componentProps: {
            title: "Earning Rule Type",
            typeConfig: EarningRuleTypesConfig,
          }
        },
        {
          key: "link_stamp_campaign_switch_key",
          component: ReactHookFormSwitch,
          componentProps: {
            valueFieldName: "linkedStampCampaign",
            title: 'For stamp campaign',
            tips: 'If ON, it can be linked to stamp campaigns only, cannot link to earning campaign.',
            extraActionOnChange: ({ dispatch, value, formContext: { setValue } }) => {
              if (value) {
                setValue("rewardType", EarningRuleRewardType.stamps, { shouldDirty: true })
              }
              dispatch(createAction("earningRuleList/updateState")({
                forStamp: value,
              }))
            }
          },
        }
      ],
    },
  ],
  languageSections: []
}

const stepTwoOnlyShowOverviewSection = ({ linkedStampCampaign, type }) => (
  linkedStampCampaign || [
    EarningRuleType.newMember,
    EarningRuleType.birthday,
    EarningRuleType.generalPurchase,
    EarningRuleType.fillingForm,
    EarningRuleType.levelUpgrade,
  ].includes(type))

export const CREATION_STEP_TWO_CONFIG = {
  // extraValidate: ({ getValues, setError, clearErrors }) => true,
  getDisplayComponentKeys: ({ formContext: { watch } }) => {
    const linkedStampCampaign = watch("linkedStampCampaign")
    const type = watch("type")
    return {
      displayNormalSectionComponents: [],
      displayLanguageSectionComponents: !stepTwoOnlyShowOverviewSection({ linkedStampCampaign, type })
        ? ["overview_key", "instruction_key", "detail_key"]
        : ["overview_key"],
    }
  },
  normalSections: [],
  languageSections: [
    {
      sectionContent: [{
        key: "overview_key",
        complexComponent: ({ language, setActiveSection, formContext: { watch } }) => (
          <OverviewSection
            onFocus={setActiveSection}
            language={language}
            areaTitle={'overview'}
            tips={
              !isEarningRuleRequireToLinkCampaign(watch("type")) &&
              i18n.t('earningRuleForStampCampaignTips', { locale: language })
            }
            requireAllLanguage={!isEarningRuleRequireToLinkCampaign(watch("type"))}
          />
        )
      },]
    },
    {
      sectionContent: [{
        key: "instruction_key",
        component: ReactHookFormMarkDownArea,
        componentProps: {
          areaTitle: "instruction",
          titleConfig: {
            label: "instruction_section_title",
            valueFieldName: "translations.{{language}}.instructionSectionTitle",
            disableFocusChange: false,
            focusChangeKey: "instructionTitle",
          },
          contentConfig: {
            label: "instruction_section_content",
            valueFieldName: "translations.{{language}}.instructionSectionContent",
            disableFocusChange: false,
            focusChangeKey: "instructionContent",
          },
          hasTranslation: true,
        },
      },]
    },
    {
      sectionContent: [{
        key: "detail_key",
        component: ReactHookFormMarkDownArea,
        componentProps: {
          areaTitle: "detail",
          titleConfig: {
            label: "detail_section_title",
            valueFieldName: "translations.{{language}}.detailSectionTitle",
            disableFocusChange: false,
            focusChangeKey: "detailsTitle",
          },
          contentConfig: {
            label: "detail_section_content",
            valueFieldName: "translations.{{language}}.detailSectionContent",
            disableFocusChange: false,
            focusChangeKey: "detailsContent",
          },
          hasTranslation: true,
        },
      },]
    },
  ]
}

const tagetCustomerSection = {
  sectionTitle: "Target customer",
  description: "Select the target customers if this earning rule is exclusively for a specific customer list, segment, or customer level. By default, the earning rule applies to everyone.",
  sectionContent: [
    {
      key: "is_exclusive_switch_key",
      component: ReactHookFormSwitch,
      componentProps: {
        valueFieldName: "isExclusive",
        title: 'Exclusive',
        tips: 'If the toggle is turned on, the earning rule will only be effective for customers from the selected customer list, segment, and levels. Note that the segment may take time to update, which could affect the final rewards delivered to the customers.'
      },
    },
    {
      key: "select_target_customer_key",
      component: TargetTypeCustomerSelector,
    },
    {
      key: "exclude_blacklist_segment_switch_key",
      component: ReactHookFormSwitch,
      componentProps: {
        valueFieldName: "excludeBlacklistSegment",
        title: 'Exclude blacklist segment',
        tips: 'If the toggle is turned on, will exclude customer in the blacklist segment.',
      },
    },
    {
      key: "blacklist_segment_selector_key",
      component: ReactHookFormMultipleSelector,
      componentProps: {
        valueFieldName: "blacklistSegments",
        loadSourceDataAction: (dispatch) => {
          dispatch(createAction('segments/getAllBlacklistSegmentList')({}));
        },
        getSourceDataFromState: (state) => state.segments.allBlackListSegmentList,
        title: "Blacklist segment",
        custom: {
          customItem: (item) => `[ID: ${item.pk}] ${item.name}`,
        },
        sortable: false
      },
    },
  ]
}

const blackoutPeriodComponent = {
  key: "blackout_period_key",
  complexComponent: ({ formContext: { watch, setValue } }) => (
    <PeriodAndWeekdaySection
      switchButtonTitle="Blackout period"
      periodValuesField="activePeriodValues"
      periodValues={watch('activePeriodValues')}
      periodValuesChange={(data) => {
        setValue('activePeriodValues', data, { shouldDirty: true });
      }}
      weekday={watch('activeWeekday')}
      weekdayChange={(data) => {
        setValue('activeWeekday', data, { shouldDirty: true });
      }}
      maxDate={watch('activeEndDate')}
      minDate={watch('activeStartDate')}
      disabledDate={!watch("isAlwaysActivePeriod")}
    />
  )
}

const activePeriodComponent = {
  key: "active_period_key",
  complexComponent: ({ formContext: { watch, setValue, formState: { errors } } }) => (
    <PeriodSection
      startErrorObject={{
        id: errors,
        error: hasError(errors, 'activeStartDate'),
        message: errorMessage(errors, 'activeStartDate'),
      }}
      endErrorObject={{
        id: 'activeEndDate',
        error: hasError(errors, 'activeEndDate'),
        message: errorMessage(errors, 'activeEndDate'),
      }}
      endDateChange={(date) => {
        setValue('activeEndDate', date, { shouldDirty: true });
      }}
      switchButtonTitle="It is an all-time active campaign"
      isAlwaysPeriod={watch("isAlwaysActivePeriod")}
      switchOnChange={(value) => {
        setValue('isAlwaysActivePeriod', value, { shouldDirty: true });
      }}
      startDate={watch('activeStartDate')}
      startDateChange={(date) => {
        setValue('activeStartDate', date, { shouldDirty: true });
      }}
      endDate={watch('activeEndDate')}
    />
  )
}

const rewardTypeRadios = {
  key: "reward_type_key",
  component: ReactHookFormTitleWithRadio,
  componentProps: {
    title: "Reward type",
    valueFieldName: "rewardType",
    defaultValue: EarningRuleRewardType.coupons,
    options: [
      { label: 'Coupons', value: EarningRuleRewardType.coupons },
      { label: 'Points', value: EarningRuleRewardType.points },
    ]
  },
}

const couponSetSelector = {
  key: "coupon_set_selector_key",
  component: CouponDropdownWithButtonAndPrompt,
  componentProps: {
    title: 'Coupon set',
    valueFieldName: "coupons",
    noTopSpace: false,
    hasAddButton: true,
    promptConfig: {
      ...getCampaignTypeSelectPromptConfig(CampaignType.couponsCampaign),
      message: 'You will leave the campaign creation process. After you create a coupon set, you can come back to create a earning rule.'
    }
  }
}

const rewardQuantityInput = {
  key: "reward_quantity_key",
  complexComponent: ({ formContext: { watch } }) => {
    const linkedStampCampaign = watch("linkedStampCampaign")
    const rewardType = watch("rewardType")
    return (
      <InputFieldControl
        name={'quantity'}
        title={linkedStampCampaign ? "Stamp quantity" : 'Quantity'}
        useDefaultFieldName
        type="number"
        unit={linkedStampCampaign
          ? "stamp per action"
          : rewardType === EarningRuleRewardType.points
            ? "number of points"
            : "coupons per number"
        }
        className="coupon-campaign-section-input"
      />
    )
  }
}

const rewardTpeInput = {
  key: "reward_tpe_key",
  component: InputFieldControl,
  componentProps: {
    name: "tpeQuantity",
    title: "Progression Points",
    useDefaultFieldName: true,
    type: "number",
    unit: "number of progression points",
    className: "coupon-campaign-section-input",
    errorId: "pointReward",
  },
}

const rewardSection = {
  sectionTitle: "REWARD",
  sectionContent: [
    rewardTypeRadios,
    couponSetSelector,
    rewardQuantityInput,
    rewardTpeInput
  ]
}

const activePeriodSection = {
  sectionTitle: "Active Period",
  description: earningRuleAvailableClue(),
  sectionContent: [
    activePeriodComponent,
    blackoutPeriodComponent,
  ]
}

const executionPrioritySection = {
  sectionTitle: "EXECUTION PRIORITY",
  sectionContent: [
    {
      key: "execution_priority_input_key",
      component: ReactHookFormTextInput,
      componentProps: {
        customClass: "width-160px",
        type: "number",
        valueFieldName: 'executionPriority',
        title: 'Execution priority',
        tips: "Set the priority for executing this earning rule relative to the other rules in the system. If a global maximum reward limit is set, only the earning rules with a higher priority will be able to issue rewards.",
        shortDescription: "Minimum value is 1. The smaller value, the higher priority."
      }
    }
  ],
}

export const CREATION_STEP_THREE_CONFIG = {
  isConfigByKey: true,
  extraValidate: ({ getValues, setError, clearErrors }) => {
    const [type, requirePromotionCode, promotionCodeUsageMaxLimit] = getValues([
      'type',
      'requirePromotionCode',
      'promotionCodeUsageMaxLimit',
    ])
    if (type === EarningRuleType.newMember && requirePromotionCode && promotionCodeUsageMaxLimit && promotionCodeUsageMaxLimit <= 0) {
      setError("promotionCodeUsageMaxLimit", {
        type: 'manual',
        message: "Please provide a valid number.",
      });
      return false
    }
    return true
  },
  getSectionKey: ({ formContext: { watch } }) => {
    return watch("type") || ""
  },
  getDisplayComponentKeys: ({ formContext: { watch } }) => {
    const earningRuleType = watch("type")
    const isExclusive = watch("isExclusive")
    const linkedStampCampaign = watch("linkedStampCampaign")
    const rewardType = watch("rewardType")
    const referralType = watch("referralType")
    const isAlwaysActivePeriod = watch("isAlwaysActivePeriod")
    const excludeBlacklistSegment = watch('excludeBlacklistSegment');
    const getRewardSectionDisplayComponents = () => ([
      "reward_type_key",
      "reward_quantity_key",
      ...(rewardType === EarningRuleRewardType.coupons ?
        [
          "coupon_set_selector_key",
        ] : [
          "reward_tpe_key",
        ]),
    ])
    let displayNormalSectionComponents = [];
    let displayLanguageSectionComponents = [];
    switch (earningRuleType) {
      case EarningRuleType.newMember:
        displayNormalSectionComponents = [
          "promotion_code_switch_key",
          "execution_priority_input_key",
          "is_exclusive_switch_key",
          ...isExclusive ? ["select_target_customer_key"] : [],
          "exclude_blacklist_segment_switch_key",
          "active_period_key",
          "blackout_period_key",
          ...getRewardSectionDisplayComponents()
        ]
        if (watch("requirePromotionCode")) {
          displayNormalSectionComponents.push("promotion_code_input_key", "promotion_code_max_times_key")
        }
        if (excludeBlacklistSegment) {
          displayNormalSectionComponents.push("blacklist_segment_selector_key")
        }
        return {
          displayNormalSectionComponents,
          displayLanguageSectionComponents
        }
      case EarningRuleType.generalPurchase:
        const lineItemRequirement = watch("lineItemRequirement")
        const inBirthMonth = watch("isInBirthMonth")
        displayNormalSectionComponents = [
          "per_transaction_key",
          "is_exclude_delivery_cost_key",
          "is_exclude_other_charges_key",
          "eligible_days_key",
          "upload_eligible_days_key",
          "store_selector_key",
          "line_item_requirement_key",
          "reward_logic_section_key",
          "in_birth_month_switch_key",
          "execution_priority_input_key",
          "reward_amount_input_area_key",
          "purchase_earning_rule_reward_limit_key",
          ...(
            inBirthMonth ? ["recurring_birthday_active_period_selector_key"] : []
          )
        ]
        if (lineItemRequirement) {
          displayNormalSectionComponents.push("sku_type_selector_key")
        }
        if (!linkedStampCampaign) {
          displayNormalSectionComponents.push(
            "is_exclusive_switch_key",
            "active_period_key",
            "blackout_period_key",
            "exclude_blacklist_segment_switch_key",
            "reward_type_key",
          )
          isExclusive && displayNormalSectionComponents.push("select_target_customer_key")
          rewardType === EarningRuleRewardType.coupons && displayNormalSectionComponents.push("coupon_set_selector_key")
          excludeBlacklistSegment && displayNormalSectionComponents.push("blacklist_segment_selector_key")
        }
        return {
          displayNormalSectionComponents,
          displayLanguageSectionComponents
        }
      case EarningRuleType.gpsCheckIn:
        return {
          displayNormalSectionComponents: [
            "execution_priority_input_key",
            "gps_latitude_key",
            "gps_longitude_key",
            "gps_radius_key",
            "limit_section_key",
            ...(linkedStampCampaign
              ? ["reward_quantity_key"] :
              rewardType === EarningRuleRewardType.coupons
                ? [
                  "reward_type_key",
                  "coupon_set_selector_key",
                  "reward_quantity_key",
                ] : [
                  "reward_type_key",
                  "reward_quantity_key",
                  "reward_tpe_key",
                ]
            ),
          ],
          displayLanguageSectionComponents
        }
      case EarningRuleType.memberReferral:
        return {
          displayNormalSectionComponents: [
            "execution_priority_input_key",
            "referral_type_key",
            ...(referralType !== 'INVITEE' ? ["max_invitees_key"] : []),
            ...(linkedStampCampaign
              ? ["reward_quantity_key"]
              : getRewardSectionDisplayComponents()
            ),
          ],
          displayLanguageSectionComponents
        }
      case EarningRuleType.birthday:
        displayNormalSectionComponents = [
          "execution_priority_input_key",
          "birthday_section_key",
          "is_exclusive_switch_key",
          ...isExclusive ? ["select_target_customer_key"] : [],
          "exclude_blacklist_segment_switch_key",
          "blacklist_segment_selector_key",
          "recurring_birthday_active_period_selector_key",
          "active_period_key",
          "blackout_period_key",
          ...(isAlwaysActivePeriod ? ["active_period_section_bottom_tips_key"] : []),
          ...(getRewardSectionDisplayComponents()),
        ]
        if (excludeBlacklistSegment) {
          displayNormalSectionComponents.push("blacklist_segment_selector_key")
        }
        return {
          displayNormalSectionComponents,
          displayLanguageSectionComponents,
        }
      case EarningRuleType.qrCodeScanning:
        return {
          displayNormalSectionComponents: [
            "execution_priority_input_key",
            "qr_code_scanning_section_key",
            "limit_section_key",
            ...(linkedStampCampaign
              ? ["reward_quantity_key"]
              : getRewardSectionDisplayComponents()
            ),
          ],
          displayLanguageSectionComponents
        }
      case EarningRuleType.fillingForm:
        displayNormalSectionComponents= [
          "execution_priority_input_key",
          "is_exclusive_switch_key",
          ...isExclusive ? ["select_target_customer_key"] : [],
          "exclude_blacklist_segment_switch_key",
          "blacklist_segment_selector_key",
          "active_period_key",
          "blackout_period_key",
          ...(getRewardSectionDisplayComponents()),
          "filling_form_section_key",
        ]
        if (excludeBlacklistSegment) {
          displayNormalSectionComponents.push("blacklist_segment_selector_key")
        }
        return {
          displayNormalSectionComponents,
          displayLanguageSectionComponents
        }
      case EarningRuleType.levelUpgrade:
        displayNormalSectionComponents = [
          "execution_priority_input_key",
          "level_upgrade_section_key",
          "is_exclusive_switch_key",
          ...isExclusive ? ["select_target_customer_key"] : [],
          "exclude_blacklist_segment_switch_key",
          "blacklist_segment_selector_key",
          "active_period_key",
          "blackout_period_key",
          ...(getRewardSectionDisplayComponents()),
          "level_upgrade_per_head_periodic_limit_section_key",
        ]
        if (excludeBlacklistSegment) {
          displayNormalSectionComponents.push("blacklist_segment_selector_key")
        }
        return {
          displayNormalSectionComponents,
          displayLanguageSectionComponents
        }
      default:
        return {
          displayNormalSectionComponents: [],
          displayLanguageSectionComponents
        }
    }
  },
  languageSections: [],
  normalSections: {
    [EarningRuleType.newMember]: [
      {
        sectionTitle: "promotion code",
        sectionContent: [
          {
            key: "promotion_code_switch_key",
            component: ReactHookFormSwitch,
            componentProps: {
              title: "Require promotion code",
              tips: "Require customer to input the correct “Promotion code” when sign up in order to earn reward.",
              valueFieldName: "requirePromotionCode",
            }
          },
          {
            key: "promotion_code_input_key",
            component: ReactHookFormTextInput,
            componentProps: {
              customClass: "width-400px",
              valueFieldName: "promotionCode",
              title: 'Promotion code',
            }
          },
          {
            key: "promotion_code_max_times_key",
            component: ReactHookFormTextInput,
            componentProps: {
              customClass: "width-160px",
              valueFieldName: "promotionCodeUsageMaxLimit",
              title: 'Maximum number of time this code can be used (optional)',
              unit: "times",
              type: "number",
            }
          },
        ],
      },
      tagetCustomerSection,
      executionPrioritySection,
      activePeriodSection,
      rewardSection,
    ],
    [EarningRuleType.generalPurchase]: [
      {
        sectionTitle: "EARNING REQUIREMENTS",
        description: "The fields below are set to an AND relationship by default, meaning that the more requirements are met, the stricter the earning rule becomes.",
        descriptionClass: "tips-message",
        sectionContent: [
          {
            key: "line_item_requirement_key",
            component: ReactHookFormSwitch,
            componentProps: {
              title: "Line item requirement",
              valueFieldName: "lineItemRequirement",
            }
          },
          {
            key: "sku_type_selector_key",
            component: SkuTypeSelector,
          },
          {
            key: "per_transaction_key",
            component: GeneralPurchasePerMin,
            componentProps: {
              containerClassName: "general-purchase-per-transaction-container",
              title: "Per Transaction",
              minSpendingName: "minSpending",
              minQuantityName: "minQuantity",
            }
          },
          {
            key: "eligible_days_key",
            component: InputFieldControl,
            componentProps: {
              name: "eligibleDays",
              useDefaultFieldName: true,
              title: "Accept receipt X days after purchase for system handling (optional)",
              tips: "If purchase date is X days before today, system will not check earning rules and issue rewards.",
              type: "number",
              unit: "days",
              className: "coupon-campaign-section-input",
            },
          },
          {
            key: "upload_eligible_days_key",
            component: InputFieldControl,
            componentProps: {
              name: "uploadEligibleDays",
              useDefaultFieldName: true,
              title: "Upload receipt X days after purchase (optional)",
              tips: "If customers should purchase and upload receipt on the same day, input 0 for X. If input 1, means accept upload receipt before 23:59 of the following day.",
              type: "number",
              unit: "days",
              className: "coupon-campaign-section-input",
            },
          },
          {
            key: "is_exclude_delivery_cost_key",
            component: ReactHookFormSwitch,
            componentProps: {
              valueFieldName: "isExcludeDeliveryCost",
              title: 'Exclude delivery cost (for online purchase)',
            },
          },
          {
            key: "is_exclude_other_charges_key",
            component: ReactHookFormSwitch,
            componentProps: {
              valueFieldName: "isExcludeOtherCharges",
              title: 'Exclude other charges (for online purchase)',
            },
          },
          {
            key: "store_selector_key",
            component: StoreMultipleSelector,
            componentProps: { valueFieldName: "selectedStores" }
          },
          {
            key: "in_birth_month_switch_key",
            component: ReactHookFormSwitch,
            componentProps: {
              valueFieldName: "isInBirthMonth",
              title: 'In birth-month',
              bottomDescription: "If turned on, only the customers, who have birthday in current month, can fulfil this requirement to earn the rewards",
            },
          },
        ]
      },
      tagetCustomerSection,
      executionPrioritySection,
      {
        sectionTitle: 'Recurring active period',
        sectionContent: [
          {
            key: 'recurring_birthday_active_period_selector_key',
            component: RecurringCampaignPeriodSelector,
            componentProps: {
              prefix: "recurringDate.active"
            }
          },
        ],
      },
      activePeriodSection,
      {
        sectionTitle: "REWARD",
        sectionContent: [
          {
            key: "reward_logic_section_key",
            component: RewardLogicSelector,
          },
          rewardTypeRadios,
          couponSetSelector,
          {
            key: "reward_amount_input_area_key",
            component: RewardAmountInputArea,
          },
        ]
      },
      {
        sectionContent: [
          {
            key: "purchase_earning_rule_reward_limit_key",
            component: PurchaseEarningRuleRewardLimitInputs,
          },
        ]
      },
    ],
    [EarningRuleType.gpsCheckIn]: [
      {
        sectionTitle: "GPS CHECK-IN",
        sectionContent: [
          {
            key: "gps_latitude_key",
            component: InputFieldControl,
            componentProps: {
              name: 'latitude',
              title: 'Latitude',
              useDefaultFieldName: true,
              type: 'number',
            }
          },
          {
            key: "gps_longitude_key",
            component: InputFieldControl,
            componentProps: {
              name: 'longitude',
              title: 'Longitude',
              useDefaultFieldName: true,
              type: 'number',
            }
          },
          {
            key: "gps_radius_key",
            component: InputFieldControl,
            componentProps: {
              name: 'radius',
              title: 'Radius',
              useDefaultFieldName: true,
              type: 'number',
              unit: "meters",
              className: 'coupon-campaign-section-input'
            }
          },
          {
            key: "limit_section_key",
            component: LimitSection,
            componentProps: {
              tips: {
                'overallLimit': 'Maximum number of times to do GPS check-in per campaign.',
                'periodicLimit': 'Maximum number of times to do GPS check-in per period.',
                'perHeadLimit': 'Maximum number of times to do GPS check-in per customer.',
                'perHeadPeriodicLimit': 'Maximum number of times to do GPS check-in per customer per period.'
              }
            }
          },
        ]
      },
      executionPrioritySection,
      rewardSection,
    ],
    [EarningRuleType.memberReferral]: [
      {
        sectionTitle: "REFERRAL",
        sectionContent: [
          {
            key: "referral_type_key",
            component: ReactHookFormTitleWithRadio,
            componentProps: {
              title: "Beneficiary",
              valueFieldName: "referralType",
              defaultValue: "INVITER",
              options: [
                { label: 'Inviter', value: 'INVITER' },
                { label: 'Invitee', value: 'INVITEE' },
                { label: 'Both', value: 'BOTH_INVITER_AND_INVITEE' },
              ]
            }
          },
          {
            key: "max_invitees_key",
            component: InputFieldControl,
            componentProps: {
              name: 'maxInvitees',
              title: 'Maximum number of invitees (optional, for inviters only)',
              useDefaultFieldName: true,
              type: 'number',
            }
          },
        ]
      },
      executionPrioritySection,
      rewardSection,
    ],
    [EarningRuleType.birthday]: [
      {
        sectionTitle: "ELIGIBLE Birth-month",
        sectionContent: [
          {
            key: "birthday_section_key",
            complexComponent: () => (<>
              <CustomTitleLabel title="Birth-month Period" />
              <label className="section-short-description">
                {'By default, the system will auto-apply this rule to the customers who have birthday in current month.'}
              </label>
            </>
            )
          },
        ]
      },
      tagetCustomerSection,
      {
        sectionTitle: 'Recurring active period',
        sectionContent: [
          {
            key: 'recurring_birthday_active_period_selector_key',
            component: RecurringCampaignPeriodSelector,
            componentProps: {
              prefix: "recurringDate.active"
            }
          },
        ],
      },
      {
        ...activePeriodSection,
        sectionContent: [
          ...activePeriodSection.sectionContent,
          {
            key: "active_period_section_bottom_tips_key",
            complexComponent: () => (
              <div className="tips-message" style={{ lineHeight: '18.2px' }}>
                {'The campaign will be active every year; Every day,\
            the system will check and the rewards will be auto distributed (add to wallet)\
            to the eligible customers,i.e. On the 1st day of every month or the rest of the\
            days in that month for those newly signed-up members who have the birthday in that month.'
                }
              </div>
            )
          }
        ]
      },
      executionPrioritySection,
      rewardSection,
    ],
    [EarningRuleType.qrCodeScanning]: [
      {
        sectionTitle: "QR CODE SCANNING",
        sectionContent: [
          {
            key: "qr_code_scanning_section_key",
            component: QRCodeScanningSection,
          },
          {
            key: "limit_section_key",
            component: LimitSection,
            componentProps: {
              tips: {
                'overallLimit': 'Maximum number of times to scan QR code check-in per campaign.',
                'periodicLimit': 'Maximum number of times to scan QR code check-in per period.',
                'perHeadLimit': 'Maximum number of times to scan QR code check-in per customer.',
                'perHeadPeriodicLimit': 'Maximum number of times to scan QR code check-in per customer per period.'
              }
            }
          },
        ]
      },
      executionPrioritySection,
      rewardSection,
    ],
    [EarningRuleType.fillingForm]: [
      tagetCustomerSection,
      activePeriodSection,
      executionPrioritySection,
      rewardSection,
      {
        sectionTitle: "LIMIT",
        sectionContent: [
          {
            key: "filling_form_section_key",
            component: LimitSection,
            componentProps: {
              overallLimit: 'Maximum number of times to fill form per campaign.',
              periodicLimit: 'Maximum number of times to fill form per period.',
              perHeadLimit: 'Maximum number of times to fill form per customer.',
              perHeadPeriodicLimit: 'Maximum number of times to fill form per customer per period.'
            }
          },
        ]
      },
    ],
    [EarningRuleType.levelUpgrade]: [
      {
        sectionTitle: "LEVEL UPGRADE",
        sectionContent: [
          {
            key: "level_upgrade_section_key",
            component: LevelUpgradeSection,
          },
        ]
      },
      tagetCustomerSection,
      activePeriodSection,
      executionPrioritySection,
      rewardSection,
      {
        sectionTitle: "LIMIT",
        sectionContent: [
          {
            key: "level_upgrade_per_head_periodic_limit_section_key",
            component: LevelUpgradePerHeadPeriodicLimitSection,
          },
        ]
      },
    ],
  },
}